import { Component, OnInit } from '@angular/core';
import { SelectItem } from 'src/app/core/models/responses/select-item';
import { Currency, ExchangeCurrencyList } from 'src/app/models/enums/currency';
import {
  AccessoriesSubTypes,
  CurrencySubTypes,
  GoldSubTypes,
  SilverSubTypes,
} from 'src/app/models/enums/currency-sub-types';
import { AlertService } from 'src/app/services/alert.service';
import { ExchangeService } from 'src/app/services/exchange.service';
import { ProfitReportResponse, ProfitByPaymentCurrencyItem } from 'src/app/models/exchange/profit-report-response';
import { NgbDateStruct, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { DatePickerTrLangService, I18n } from 'src/app/services/date-picker-tr-lang.service';
import { getCurrencySubTypeItem } from 'src/app/core/helpers/common-functions';

@Component({
  selector: 'app-exchange-profit-report',
  templateUrl: './exchange-profit-report.component.html',
  styleUrls: ['./exchange-profit-report.component.scss'],
  providers: [
    I18n,
    { provide: NgbDatepickerI18n, useClass: DatePickerTrLangService },
  ],
})
export class ExchangeProfitReportComponent implements OnInit {
  defaultValue = null;
  currencyEnum = Currency;
  currencyList = ExchangeCurrencyList;
  subTypeList: CurrencySubTypes[] = [];

  beginDateModel: NgbDateStruct;
  endDateModel: NgbDateStruct;

  filter = {
    startDate: null as Date,
    endDate: null as Date,
    paymentCurrency: null as Currency,
    paymentSubType: null as CurrencySubTypes,
  };

  profitReport: ProfitReportResponse;
  isLoading = false;
  paymentSubTypes: CurrencySubTypes[] = [];

  constructor(
    private exchangeService: ExchangeService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.setDefaultDates();
  }

  setDefaultDates(): void {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    this.beginDateModel = {
      year: firstDayOfMonth.getFullYear(),
      month: firstDayOfMonth.getMonth() + 1,
      day: firstDayOfMonth.getDate(),
    };

    this.endDateModel = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };

    this.filter.startDate = firstDayOfMonth;
    this.filter.endDate = today;
  }

  onPaymentCurrencyChanged(): void {
    if (this.filter.paymentCurrency) {
      const subTypeItem = getCurrencySubTypeItem(this.filter.paymentCurrency);
      this.paymentSubTypes = subTypeItem?.subtypes || [];
      this.filter.paymentSubType = null;
    } else {
      this.paymentSubTypes = [];
      this.filter.paymentSubType = null;
    }
  }

  generateReport(): void {
    if (!this.filter.startDate || !this.filter.endDate) {
      this.alertService.showError('Lütfen başlangıç ve bitiş tarihini seçiniz.');
      return;
    }

    if (this.filter.startDate > this.filter.endDate) {
      this.alertService.showError('Başlangıç tarihi bitiş tarihinden büyük olamaz.');
      return;
    }

    this.isLoading = true;
    const request = {
      startDate: this.filter.startDate,
      endDate: this.filter.endDate,
      paymentCurrency: this.filter.paymentCurrency,
      paymentSubType: this.filter.paymentSubType,
    };

    this.exchangeService.getProfitReport(request).subscribe(
      (response) => {
        this.profitReport = response.data;
        this.isLoading = false;
        if (this.profitReport) {
          this.alertService.showSuccess('Kar raporu başarıyla yüklendi.');
        }
      },
      (error) => {
        this.isLoading = false;
        this.alertService.showError('Kar raporu yüklenirken hata oluştu.');
      }
    );
  }

  exportToExcel(): void {
    if (!this.profitReport || this.profitReport.profitByPaymentCurrency.length === 0) {
      this.alertService.showError('Dışa aktarılacak veri yok.');
      return;
    }

    const csv = this.generateCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `kar-raporu-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private generateCSV(): string {
    let csv = 'Kar Raporu\n';
    csv += `Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}\n`;
    csv += `Başlangıç: ${this.filter.startDate.toLocaleDateString('tr-TR')}\n`;
    csv += `Bitiş: ${this.filter.endDate.toLocaleDateString('tr-TR')}\n\n`;

    csv += 'Özet\n';
    csv += `Toplam İşlem: ${this.profitReport.totalTransactions}\n`;
    csv += `Toplam Kar TL: ${this.profitReport.totalProfitTL.toFixed(2)}\n`;
    csv += `Toplam Kar USD: ${this.profitReport.totalProfitUSD.toFixed(2)}\n`;
    csv += `Toplam Kar HAS: ${this.profitReport.totalProfitHAS.toFixed(3)}\n`;
    if (this.profitReport.currentHasToTL) {
      const today = this.profitReport.totalProfitHAS * this.profitReport.currentHasToTL;
      csv += `Bugünkü Kurla TL: ${today.toFixed(2)}\n`;
      csv += `1 Gram Has TL: ${this.profitReport.currentHasToTL.toFixed(2)}\n`;
    }
    csv += `Islem Farki HAS: ${(this.profitReport.totalMarketDifferenceHAS ?? 0).toFixed(3)}\n`;
    csv += `TL ve USD kar sütunları işlem günü kuruyla çevrilmiştir.\n`;
    csv += `Islem farki kar degildir: uygulanan kurun gunun kurundan sapmasidir.\n\n`;

    csv += 'Satılan Birim,İşlem Sayısı,Satılan Miktar,Kar TL,Kar USD,Kar HAS,Islem Farki HAS\n';
    this.profitReport.profitByPaymentCurrency.forEach((item) => {
      csv += `${item.paymentCurrency}/${item.paymentSubType || 'N/A'},${item.count},${item.totalAmount.toFixed(2)},${item.profitTL.toFixed(2)},${item.profitUSD.toFixed(2)},${item.profitHAS.toFixed(3)},${(item.marketDifferenceHAS ?? 0).toFixed(3)}\n`;
    });

    return csv;
  }

  onBeginDateChange(event: NgbDateStruct): void {
    if (event) {
      this.filter.startDate = new Date(event.year, event.month - 1, event.day);
    } else {
      this.filter.startDate = null;
    }
  }

  onEndDateChange(event: NgbDateStruct): void {
    if (event) {
      this.filter.endDate = new Date(event.year, event.month - 1, event.day);
    } else {
      this.filter.endDate = null;
    }
  }

  clearFilter(): void {
    this.filter = {
      startDate: null,
      endDate: null,
      paymentCurrency: null,
      paymentSubType: null,
    };
    this.paymentSubTypes = [];
    this.profitReport = null;
    this.setDefaultDates();
  }
}
